import { useMutation } from "@tanstack/react-query";
import { useId, useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router";

import { claimCompany } from "../../api/claims";
import { ApiError, errorText } from "../../api/client";
import { addCompany } from "../../api/companies";
import type { CompanyDetail } from "../../api/companies";
import { Button, buttonClass } from "../../components/Button";
import { Field, FormError } from "../../components/Field";
import { useToast } from "../../components/Toast";
import { nl } from "../../i18n/nl";
import { formatDate } from "../../lib/format";
import { useRefreshAfterChange } from "../claims/queries";
import { MunicipalityPicker } from "../profile/PreferenceFields";
import { useReport } from "../report/ReportProvider";
import styles from "./search.module.css";

const t = nl.addCompany;
const KBO_PUBLIC_SEARCH = "https://kbopub.economie.fgov.be/kbopub/zoeknaamfonetischform.html";

function existingId(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  if (error.code !== "company_exists" && error.code !== "possible_duplicate") return null;
  const id = error.details?.id;
  return typeof id === "string" ? id : null;
}

/** What to do with a freshly added company: claim, tip or "already had contact". */
function NextStep({ company, onDone }: { company: CompanyDetail; onDone: () => void }) {
  const toast = useToast();
  const refresh = useRefreshAfterChange();
  const openReport = useReport();
  const claim = useMutation({
    mutationFn: () => claimCompany(company.id),
    onSuccess: (created) => {
      refresh();
      toast({ message: nl.claims.claimed(company.name, formatDate(created.expires_at)) });
      onDone();
    },
    onError: (error) => {
      toast({ message: errorText(error) });
    },
  });
  return (
    <section className={styles.next} aria-labelledby="add-next-title">
      <h2 id="add-next-title" className={styles.sectionTitle} tabIndex={-1}>
        {t.nextTitle}
      </h2>
      {company.kbo_number === null && !company.is_sole_proprietorship && <p className={styles.muted}>{t.review}</p>}
      <div className={styles.nextActions}>
        <Button
          variant="primary"
          disabled={claim.isPending}
          onClick={() => {
            claim.mutate();
          }}
        >
          {t.claim}
        </Button>
        <Button
          onClick={() => {
            onDone();
            openReport(company.id);
          }}
        >
          {t.tip}
        </Button>
        <Button
          onClick={() => {
            onDone();
            openReport(company.id);
          }}
        >
          {t.contact}
        </Button>
        <Link className={buttonClass("ghost")} to={`/bedrijven/${company.id}`} onClick={onDone}>
          {t.later}
        </Link>
      </div>
    </section>
  );
}

export function AddCompanyForm({ initialName = "", onDone }: { initialName?: string; onDone: () => void }) {
  const id = useId();
  const toast = useToast();
  const refresh = useRefreshAfterChange();
  const [name, setName] = useState(initialName);
  const [municipality, setMunicipality] = useState<string | null>(null);
  const [kbo, setKbo] = useState("");
  const [sole, setSole] = useState(false);
  const [missing, setMissing] = useState(false);
  const [created, setCreated] = useState<CompanyDetail | null>(null);
  const add = useMutation({
    mutationFn: (confirm: boolean) =>
      addCompany({
        name: name.trim(),
        municipality: municipality ?? "",
        kbo_number: kbo.trim() || null,
        is_sole_proprietorship: sole,
        confirm,
      }),
    onSuccess: (company) => {
      refresh();
      toast({ message: t.added(company.name) });
      setCreated(company);
    },
  });

  if (created) return <NextStep company={created} onDone={onDone} />;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (name.trim().length < 2 || !municipality) {
      setMissing(true);
      return;
    }
    setMissing(false);
    add.mutate(false);
  }

  const duplicateOf = existingId(add.error);
  const code = add.error instanceof ApiError ? add.error.code : null;
  return (
    <form className={styles.form} onSubmit={submit} noValidate aria-labelledby={`${id}-title`}>
      <h2 id={`${id}-title`} className={styles.sectionTitle}>
        {t.title}
      </h2>
      <Field
        label={t.name}
        placeholder={t.namePlaceholder}
        maxLength={200}
        value={name}
        error={missing && name.trim().length < 2 ? nl.auth.required : null}
        onChange={(event) => {
          setName(event.target.value);
        }}
      />
      <MunicipalityPicker
        label={t.municipality}
        help={t.municipalityHelp}
        value={municipality}
        onChange={setMunicipality}
        error={missing && !municipality ? nl.auth.required : null}
      />
      <Field
        label={t.kbo}
        help={t.kboHelp}
        inputMode="numeric"
        placeholder="0712.345.630"
        maxLength={20}
        value={kbo}
        onChange={(event) => {
          setKbo(event.target.value);
        }}
      />
      <p className={styles.muted}>
        <a href={KBO_PUBLIC_SEARCH} target="_blank" rel="noreferrer">
          {t.kboLink}
        </a>
      </p>
      <label className={styles.check} htmlFor={`${id}-sole`}>
        <input
          id={`${id}-sole`}
          type="checkbox"
          checked={sole}
          onChange={(event) => {
            setSole(event.target.checked);
          }}
        />
        <span className={styles.checkLabel}>{t.sole}</span>
        <span className={styles.muted}>{t.soleHelp}</span>
      </label>
      {duplicateOf ? (
        <div className={styles.warning} role="alert">
          <p>{code === "company_exists" ? t.exists : t.duplicate}</p>
          <div className={styles.nextActions}>
            <Link className={buttonClass("primary")} to={`/bedrijven/${duplicateOf}`} onClick={onDone}>
              {t.openExisting}
            </Link>
            {code === "possible_duplicate" && (
              <Button
                disabled={add.isPending}
                onClick={() => {
                  add.mutate(true);
                }}
              >
                {t.addAnyway}
              </Button>
            )}
          </div>
        </div>
      ) : (
        <FormError message={add.error ? errorText(add.error) : null} />
      )}
      <div>
        <Button type="submit" variant="primary" disabled={add.isPending}>
          {add.isPending ? t.submitting : t.submit}
        </Button>
      </div>
    </form>
  );
}
